import i18n from "multitenancy/i18n";

import { baseURL } from "services/api";
import { hearingFileService } from "services/hearingFile";

export interface HearingFileLinkApiItem {
  hearingFileId: number;
  hearingId?: number;
  url?: string | null;
  title?: string | null;
  recordedDate?: string | null;
  expireIn?: string | null;
  type?: string | null;
}

export interface RecordingViewerAttachment {
  filename?: string | null;
  title?: string | null;
  hearingFileId: number;
  size?: number;
  type?: string | number | null;
  url?: string | null;
  recordedDate?: string | null;
  expireIn?: string | null;
}

export interface RecordingViewerContentResponse {
  mediaType: "VIDEO" | "MESSAGE" | "DOWNLOAD";
  videoUrl?: string;
  title?: string;
  message?: string;
  hearingDate?: string;
  expireIn?: string;
  hearingFileId?: number;
  hearingId?: number;
  type?: string;
  attachments?: RecordingViewerAttachment[];
}

export interface RecordingViewerRawContent {
  mediaType?: string | null;
  type?: string | null;
  videoUrl?: string | null;
  mediaUrl?: string | null;
  url?: string | null;
  mimeType?: string | null;
  contentType?: string | null;
  title?: string | null;
  heading?: string | null;
  description?: string | null;
  subtitle?: string | null;
  message?: string | null;
  text?: string | null;
  hearingNumber?: string | null;
  audienceNumber?: string | null;
  procedureNumber?: string | null;
  hearingDate?: string | null;
  audienceDate?: string | null;
  recordedAt?: string | null;
  date?: string | null;
}

export interface RecordingViewerServiceResponse {
  success: boolean;
  data?: RecordingViewerContentResponse;
  message?: string;
  requiresMfa?: boolean;
  cooldownSeconds?: number;
}

interface HearingFileLinkResponse {
  success: boolean;
  message?: string;
  data?: unknown;
  requiresMfa?: boolean;
  isCooldown?: boolean;
}

export const RecordingViewerService = {
  getContent: async (
    token: string,
    code?: string,
  ): Promise<RecordingViewerServiceResponse> => {
    try {
      const response: HearingFileLinkResponse =
        await hearingFileService.getHearingFileLinkByToken(token, code);

      if (response.requiresMfa) {
        const cooldownMatch = response.isCooldown
          ? response.message?.match(/(\d+)\s*segundos/)
          : null;
        return {
          success: false,
          requiresMfa: true,
          message:
            response.message || i18n.t("recordingViewer.enterCodeDescription"),
          cooldownSeconds: cooldownMatch
            ? parseInt(cooldownMatch[1], 10)
            : undefined,
        };
      }

      if (!response.success || !response.data) {
        return {
          success: false,
          message: response.message || i18n.t("recordingViewer.loadError"),
        };
      }

      const apiData = response.data as
        | HearingFileLinkApiItem
        | HearingFileLinkApiItem[];

      const mapAttachment = (
        item: HearingFileLinkApiItem,
      ): RecordingViewerAttachment => ({
        hearingFileId: item.hearingFileId,
        url: item.url ?? undefined,
        title: item.title ?? undefined,
        type: item.type ?? undefined,
        recordedDate: item.recordedDate ?? undefined,
        expireIn: item.expireIn ?? undefined,
      });

      let content: RecordingViewerContentResponse;

      if (Array.isArray(apiData)) {
        const items = apiData;
        const mainItem =
          items.find((item) => item.type === "Record" && item.url) ||
          items.find((item) => item.type === "hearingFileType" && item.url) ||
          items.find((item) => item.url) ||
          items[0];

        const attachments = items
          .filter((item) => !item.url)
          .map(mapAttachment);

        const mediaType = mainItem?.url ? "VIDEO" : "MESSAGE";
        const proxyVideoUrl = mainItem?.url
          ? `${baseURL}/v1/HearingFile/StreamSharedVideo?sharedToken=${token}`
          : undefined;

        content = {
          mediaType,
          videoUrl: proxyVideoUrl,
          title: mainItem?.title ?? undefined,
          hearingDate: mainItem?.recordedDate ?? undefined,
          expireIn: mainItem?.expireIn ?? undefined,
          hearingFileId: mainItem?.hearingFileId,
          hearingId: mainItem?.hearingId,
          type: mainItem?.type ?? undefined,
          attachments,
        };
      } else {
        let mediaType: "VIDEO" | "MESSAGE" | "DOWNLOAD" = "VIDEO";

        if (apiData.type === "hearingFileType" || apiData.type === "Record") {
          mediaType = "VIDEO";
        } else {
          mediaType = "DOWNLOAD";
        }

        const proxyVideoUrlSingle = apiData.url
          ? `${baseURL}/v1/HearingFile/StreamSharedVideo?sharedToken=${token}`
          : undefined;

        content = {
          mediaType,
          videoUrl: proxyVideoUrlSingle,
          title: apiData.title ?? undefined,
          hearingDate: apiData.recordedDate ?? undefined,
          expireIn: apiData.expireIn ?? undefined,
          hearingFileId: apiData.hearingFileId,
          hearingId: apiData.hearingId,
          type: apiData.type ?? undefined,
          attachments: [],
        };
      }

      return {
        success: true,
        data: content,
      };
    } catch (error) {
      return {
        success: false,
        message: i18n.t("recordingViewer.loadTokenError"),
      };
    }
  },
};
